/** Automaid error envelope: `{ error: string, errors?: { [field]: string[] } }`. */
export interface ErrorEnvelope {
  error?: string;
  errors?: Record<string, unknown>;
}

/**
 * One entry of the live API's field errors. The documented envelope is
 * `{ error, errors: { field: [msg] } }`, but 422s actually arrive keyed by field at the TOP
 * level with arrays of objects, e.g.
 *   {"payment_method":[{"type":"invalid","message":"account does not support payment method"}]}
 *   {"(root)":[{"type":"required","message":"(root): payment_method is required",
 *               "details":{"property":"payment_method"}}]}
 * Both shapes are accepted (captured from the API 2026-09-01, confirmed live 2026-09-11).
 */
interface LiveFieldError {
  message?: unknown;
  description?: unknown;
  details?: { property?: unknown; field?: unknown };
}

/** Keys that are envelope metadata rather than field names. */
const RESERVED_KEYS = new Set([
  'error',
  'errors',
  'message',
  'code',
  'status',
  'statusText',
  'detail',
  'details',
  'success',
]);

/** The API prefixes messages with the field name ("zip: is invalid"); drop it for display. */
function tidyMessage(text: string, key: string): string {
  const trimmed = text.trim();
  const prefix = `${key}:`;
  return trimmed.startsWith(prefix) ? trimmed.slice(prefix.length).trim() : trimmed;
}

function messagesFor(key: string, value: unknown): string[] {
  const list = Array.isArray(value) ? value : [value];
  const messages: string[] = [];
  for (const entry of list) {
    if (typeof entry === 'string') {
      if (entry.trim()) messages.push(tidyMessage(entry, key));
      continue;
    }
    if (entry && typeof entry === 'object') {
      const e = entry as LiveFieldError;
      const text =
        typeof e.message === 'string'
          ? e.message
          : typeof e.description === 'string'
            ? e.description
            : '';
      // String(entry) would render "[object Object]" — never show that to a customer.
      if (text.trim()) messages.push(tidyMessage(text, key));
    }
  }
  return messages;
}

/** `(root)` errors name their real field in `details.property`. */
function resolveKey(key: string, value: unknown): string {
  if (key !== '(root)') return key;
  const first = (Array.isArray(value) ? value[0] : value) as LiveFieldError | undefined;
  const property = first?.details?.property ?? first?.details?.field;
  return typeof property === 'string' && property.trim() ? property.trim() : key;
}

/** Field-keyed error maps, from either the documented wrapper or the live top-level shape. */
function collectFieldErrors(source: Record<string, unknown>): FieldErrors {
  const fieldErrors: FieldErrors = {};
  for (const [rawKey, value] of Object.entries(source)) {
    if (RESERVED_KEYS.has(rawKey)) continue;
    const messages = messagesFor(rawKey, value);
    if (!messages.length) continue;
    const key = resolveKey(rawKey, value);
    fieldErrors[key] = [...(fieldErrors[key] ?? []), ...messages];
  }
  return fieldErrors;
}

export type FieldErrors = Record<string, string[]>;

export class ApiError extends Error {
  override readonly name = 'ApiError';
  constructor(
    readonly status: number,
    /** Human-readable message from the envelope (or a fallback). */
    readonly errorMessage: string,
    /** Field-keyed messages from `errors`, ready for per-field display. */
    readonly fieldErrors: FieldErrors,
    /** Raw parsed body (object, string, or null). */
    readonly body: unknown,
    /** From `Retry-After` when the server exposes it; otherwise null. */
    readonly retryAfterMs: number | null = null,
  ) {
    super(`${status}: ${errorMessage}`);
  }
  get isValidation(): boolean {
    return this.status === 422;
  }
  get isRateLimited(): boolean {
    return this.status === 429;
  }
  /** `POST /booking/location` answers 406 when the address is outside a blocked service area. */
  get isOutOfArea(): boolean {
    return this.status === 406;
  }
  /** No refresh endpoint exists, so a 401 on an authed call means the session is over. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  /** On `POST /login` this is overloaded: an account lockout OR a 2FA challenge. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get hasFieldErrors(): boolean {
    return Object.keys(this.fieldErrors).length > 0;
  }
}

/**
 * The customer's bearer was rejected. Raised in place of a 401 ApiError by the portal API layer,
 * which has already cleared the stored session by the time this surfaces.
 */
export class SessionExpiredError extends Error {
  override readonly name = 'SessionExpiredError';
}

export class NetworkError extends Error {
  override readonly name = 'NetworkError';
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message);
  }
}

/** Thrown when a write would hit production without explicit opt-in. */
export class ProductionGuardError extends Error {
  override readonly name = 'ProductionGuardError';
}

export function isAbortError(err: unknown): boolean {
  return (
    typeof err === 'object' && err !== null && (err as { name?: unknown }).name === 'AbortError'
  );
}

function defaultMessage(status: number, statusText: string): string {
  if (status === 429) return 'Too many requests — please wait a moment and try again.';
  if (status >= 500) return 'The booking service is temporarily unavailable.';
  return statusText || `Request failed (${status})`;
}

export function parseErrorEnvelope(
  status: number,
  statusText: string,
  body: unknown,
): { message: string; fieldErrors: FieldErrors } {
  if (body && typeof body === 'object') {
    const env = body as ErrorEnvelope;
    // Documented wrapper first; otherwise the body itself is the field map (the live shape).
    const fieldErrors =
      env.errors && typeof env.errors === 'object'
        ? collectFieldErrors(env.errors as Record<string, unknown>)
        : collectFieldErrors(body as Record<string, unknown>);
    const message =
      typeof env.error === 'string' && env.error.trim()
        ? env.error.trim()
        : defaultMessage(status, statusText);
    return { message, fieldErrors };
  }
  if (typeof body === 'string' && body.trim()) {
    return { message: body.trim().slice(0, 500), fieldErrors: {} };
  }
  return { message: defaultMessage(status, statusText), fieldErrors: {} };
}
